import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, Filter, Package, X } from "lucide-react";
import {
  useAssets,
  useAssetSummary,
  useAssetCategories,
  useEntities,
} from "../../hooks/useAssets";
import { AssetSummary } from "../../components/asset/AssetSummary.jsx";
import { useAuth } from "../../context/AuthContext";
import { HolderSelector } from "../../components/HolderSelector";
import { Button, Badge, Spinner } from "../../components/ui/index.jsx";
import {
  StatusBadge,
  ASSET_STATUS_OPTIONS,
} from "../../components/asset/AssetHelpers.jsx";
import { formatDate, formatIDR } from "../../utils/format";
import { cn } from "../../utils/cn";
import {
  DATA_ISSUE_LABELS,
  WARRANTY_FILTER_LABELS,
  OWNERSHIP_CLASS_LABELS,
} from "../../utils/constants";

const CAN_CREATE = ["ADMIN", "GA"];
// Roles that see prices, ownership and the Asset Summary (backend: canViewFinancials)
const CAN_SEE_FINANCE = ["ADMIN", "GA", "FINANCE", "EXECUTIVE"];

// Table cells use the short form; the summary panel and the filter carry the
// full names Finance uses.
const OWNERSHIP_SHORT = {
  PAID_OFF: "Paid off",
  UNDER_INSTALLMENT: "Under installment",
  RENTED_LEASED: "Rented / leased",
};

// The server's current month is a UTC month, so the picker's upper bound is too
const currentPeriod = () => new Date().toISOString().slice(0, 7);

// Filters live in the URL (not component state) so they survive
// list -> detail -> back navigation for free via the browser's own
// history, and a hard refresh / pasted link restores the same view.
// A fresh sidebar click (Link to plain "/assets", no query string)
// resets to these defaults, same as before this change.
const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";
const DEFAULT_LIMIT = 25;

export default function AssetsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const params = {
    page: Number(searchParams.get("page")) || 1,
    limit: Number(searchParams.get("limit")) || DEFAULT_LIMIT,
    search: searchParams.get("search") || "",
    entityId: searchParams.get("entityId") || "",
    categoryId: searchParams.get("categoryId") || "",
    status: searchParams.get("status") || "",
    holderId: searchParams.get("holderId") || "",
    // ANY = has at least one data-issue flag; otherwise one specific flag
    dataIssue: searchParams.get("dataIssue") || "",
    warranty: searchParams.get("warranty") || "",
    ownership: searchParams.get("ownership") || "",
    notValued: searchParams.get("notValued") || "",
    sortBy: searchParams.get("sortBy") || DEFAULT_SORT_BY,
    sortOrder: searchParams.get("sortOrder") || DEFAULT_SORT_ORDER,
  };

  const [searchInput, setSearchInput] = useState(params.search);

  // Keep the search box in sync with the URL on back/forward navigation
  // (the input's own typing already updates via setSearchInput directly).
  useEffect(() => {
    setSearchInput(params.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("search")]);

  const { data, isLoading, isError } = useAssets(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "")),
  );

  // Asset Summary: follows the entity and the month only. Search, condition,
  // holder and the other filters narrow the list below but never the totals.
  const showFinance = CAN_SEE_FINANCE.includes(user?.role);
  const period = searchParams.get("period") || "";
  const summaryQuery = useAssetSummary({
    entityId: params.entityId,
    period,
    enabled: showFinance,
  });
  // what the summary's links carry: its own scope only, not the other filters
  const summaryScope = new URLSearchParams();
  if (params.entityId) summaryScope.set("entityId", params.entityId);
  if (period) summaryScope.set("period", period);

  const { data: categories = [] } = useAssetCategories();
  const { data: entities = [] } = useEntities();

  const assets = data?.data ?? [];
  const pagination = data?.pagination;

  const hasActiveFilters =
    !!params.search ||
    !!params.entityId ||
    !!params.categoryId ||
    !!params.status ||
    !!params.holderId ||
    !!params.dataIssue ||
    !!params.warranty ||
    !!params.ownership ||
    !!params.notValued ||
    params.page !== 1;

  function setFilter(key, value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete("page"); // any filter change starts back at page 1
      return next;
    });
  }

  function goToPage(page) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(page));
      return next;
    });
  }

  // Click a heading to sort; a second click flips the direction.
  function sortBy(column) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const flip = params.sortBy === column && params.sortOrder === "asc";
      next.set("sortBy", column);
      next.set("sortOrder", params.sortBy === column ? (flip ? "desc" : "asc") : "asc");
      next.delete("page");
      return next;
    });
  }

  function resetFilters() {
    setSearchInput("");
    setSearchParams({});
  }

  function handleSearch(e) {
    e.preventDefault();
    setFilter("search", searchInput);
  }

  return (
    <div>
      {/* Header: the scope of the page — which entity, and for finance which month */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Assets</h1>
          {pagination && (
            <p className="text-sm text-slate-500 mt-0.5">
              {pagination.total.toLocaleString()} {pagination.total === 1 ? "asset" : "assets"} in this list
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            aria-label="Entity"
            value={params.entityId}
            onChange={(e) => setFilter("entityId", e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="">All entities</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.code} — {e.name}
              </option>
            ))}
          </select>
          {showFinance && (
            <input
              type="month"
              aria-label="Period"
              title="Month for acquisition cost and book value"
              value={period || currentPeriod()}
              min={summaryQuery.data?.earliestPeriod ?? undefined}
              max={currentPeriod()}
              onChange={(e) => {
                const v = e.target.value;
                // blank or back to this month = the default, kept out of the URL
                setFilter("period", v && v !== currentPeriod() ? v : "");
              }}
              className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            />
          )}
        {CAN_CREATE.includes(user?.role) && (
          <Button onClick={() => navigate("/assets/new")}>
            <Plus className="h-4 w-4" />
            Add Asset
          </Button>
        )}
        </div>
      </div>

      {showFinance && (
        <AssetSummary
          summary={summaryQuery.data}
          isLoading={summaryQuery.isLoading}
          isError={summaryQuery.isError}
          scope={summaryScope}
          status={params.status}
          ownership={params.ownership}
          onSelectStatus={(v) => setFilter("status", v)}
          onSelectOwnership={(v) => setFilter("ownership", v)}
        />
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-64">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search name, number, serial..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <Button type="submit" variant="secondary" size="sm">
              Search
            </Button>
          </form>

          {/* Category filter */}
          <select
            value={params.categoryId}
            onChange={(e) => setFilter("categoryId", e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={params.status}
            onChange={(e) => setFilter("status", e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="">All Conditions</option>
            {ASSET_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {/* Ownership filter */}
          {showFinance && (
            <select
              value={params.ownership}
              onChange={(e) => setFilter("ownership", e.target.value)}
              className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              <option value="">All Ownership</option>
              {Object.entries(OWNERSHIP_CLASS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}

          {/* Warranty filter */}
          <select
            value={params.warranty}
            onChange={(e) => setFilter("warranty", e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="">All Warranties</option>
            {Object.entries(WARRANTY_FILTER_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          {/* Data status filter — GA's work queue for undocumented assets */}
          <select
            value={params.dataIssue}
            onChange={(e) => setFilter("dataIssue", e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="">All Data Status</option>
            <option value="ANY">Any data issue</option>
            {Object.entries(DATA_ISSUE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          {/* Holder filter */}
          <div className="w-56">
            <HolderSelector
              value={params.holderId || null}
              onChange={(id) => setFilter("holderId", id || "")}
              placeholder="Filter by holder..."
            />
          </div>

          {params.notValued && (
            <button
              type="button"
              onClick={() => setFilter("notValued", "")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200"
              title="Clear this filter"
            >
              Not valued — missing date, price or useful life <X className="h-3.5 w-3.5" />
            </button>
          )}

          {hasActiveFilters && (
            <Button variant="secondary" size="sm" onClick={resetFilters}>
              <X className="h-4 w-4" /> Reset Filters
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="text-center py-20 text-rose-600 text-sm">
            Failed to load assets. Please refresh.
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-20">
            <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No assets found.</p>
            {CAN_CREATE.includes(user?.role) && (
              <Button
                className="mt-4"
                size="sm"
                onClick={() => navigate("/assets/new")}
              >
                Add your first asset
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th
                    scope="col"
                    aria-sort={
                      params.sortBy === "rowNumber"
                        ? params.sortOrder === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    className="text-left px-2.5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                  >
                    <button
                      type="button"
                      onClick={() => sortBy("rowNumber")}
                      title="Permanent running number — click to sort"
                      className="uppercase tracking-wide hover:text-slate-800"
                    >
                      No.
                      {params.sortBy === "rowNumber" &&
                        (params.sortOrder === "asc" ? " ↑" : " ↓")}
                    </button>
                  </th>
                  {[
                    ["Asset Number"],
                    ["Name"],
                    ["Entity"],
                    ["Category"],
                    ["Holder"],
                    ["Condition"],
                    ...(showFinance
                      ? [
                          ["Ownership"],
                          ["Cost", true, "Acquisition cost (entity acquisition)"],
                          ["Book value", true, "Book value as of today"],
                        ]
                      : []),
                  ].map(([h, right, hint]) => (
                    <th
                      key={h}
                      scope="col"
                      title={hint}
                      className={cn(
                        "px-2.5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap",
                        right ? "text-right" : "text-left",
                      )}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assets.map((asset) => (
                  <tr
                    key={asset.id}
                    onClick={() =>
                      // remember this exact list view (filters, page) so the
                      // detail page's back button can return to it
                      navigate(`/assets/${asset.id}`, {
                        state: { from: location.pathname + location.search },
                      })
                    }
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-2.5 py-3 text-xs text-slate-500 tabular-nums whitespace-nowrap">
                      {asset.rowNumber}
                    </td>
                    <td className="px-2.5 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">
                      {asset.assetNumber}
                    </td>
                    <td className="px-2.5 py-3">
                      <div className="flex items-center gap-2.5">
                        {asset.primaryImage ? (
                          <img
                            src={asset.primaryImage.fileUrl}
                            alt=""
                            className="h-7 w-7 rounded object-cover shrink-0 bg-slate-100"
                          />
                        ) : (
                          <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center shrink-0">
                            <Package className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p
                            className="font-medium text-slate-900 truncate max-w-32"
                            title={asset.name}
                          >
                            {asset.name}
                          </p>
                          {(asset.brand || asset.model) && (
                            <p className="text-xs text-slate-400 truncate max-w-32">
                              {[asset.brand, asset.model]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          )}
                          {asset.dataIssues?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1 max-w-36">
                              {asset.dataIssues.map((i) => (
                                <Badge key={i} variant="warning">
                                  {DATA_ISSUE_LABELS[i] ?? i}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2.5 py-3 whitespace-nowrap">
                      <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                        {asset.entity?.code}
                      </span>
                    </td>
                    <td className="px-2.5 py-3 text-slate-600 max-w-28 truncate" title={asset.category?.name}>
                      {asset.category?.name ?? (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-2.5 py-3 text-slate-600 max-w-24 truncate" title={asset.holder?.name}>
                      {asset.holder?.name ?? (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-2.5 py-3 whitespace-nowrap">
                      {/* the common state is plain text; only exceptions get a pill */}
                      {asset.status === "ACTIVE" ? (
                        <span className="text-slate-600">Active</span>
                      ) : (
                        <StatusBadge status={asset.status} />
                      )}
                    </td>
                    {showFinance && (
                      <>
                        <td className="px-2.5 py-3 whitespace-nowrap">
                          {asset.ownershipClass === "UNDER_INSTALLMENT" ? (
                            <div>
                              <Badge variant="primary">
                                {OWNERSHIP_SHORT.UNDER_INSTALLMENT}
                              </Badge>
                              {(asset.installmentStartDate ||
                                asset.installmentEndDate) && (
                                <p className="text-xs text-slate-400 mt-1 tabular-nums">
                                  {asset.installmentStartDate
                                    ? formatDate(asset.installmentStartDate, "dd MMM yy")
                                    : "—"}
                                  {" – "}
                                  {asset.installmentEndDate
                                    ? formatDate(asset.installmentEndDate, "dd MMM yy")
                                    : "—"}
                                </p>
                              )}
                            </div>
                          ) : asset.ownershipClass === "RENTED_LEASED" ? (
                            <Badge variant="default">
                              {OWNERSHIP_SHORT.RENTED_LEASED}
                            </Badge>
                          ) : (
                            <span className="text-slate-600">
                              {OWNERSHIP_SHORT.PAID_OFF}
                            </span>
                          )}
                        </td>
                        <td className="px-2.5 py-3 text-right text-slate-900 tabular-nums whitespace-nowrap">
                          {asset.entityPurchasePrice != null ? (
                            formatIDR(asset.entityPurchasePrice)
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-2.5 py-3 text-right text-slate-900 tabular-nums whitespace-nowrap">
                          {asset.depreciation ? (
                            formatIDR(asset.depreciation.bookValue)
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
